import { useEffect, useRef, useState } from "react"
import { Head } from "@inertiajs/react"
import { Briefcase, Check, Gift } from "lucide-react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { AuthHeading } from "@/components/tuw/auth/AuthHeading"
import AuthLayout from "@/components/tuw/auth/AuthLayout"
import { CsrfField, PasswordField, TextField } from "@/components/tuw/auth/fields"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { captureAffiliate } from "@/lib/affiliate"
import { cn } from "@/lib/utils"

const MIN_PASSWORD = 6
const TERMS_URL = "https://theurbanwriters.com/pages/terms-of-service"
const PRIVACY_URL = "https://theurbanwriters.com/pages/privacy-policy"

const LINK_CLASS =
  "font-semibold text-foreground underline decoration-line-2 underline-offset-[3px] hover:decoration-foreground"

// Today's reCAPTCHA widget: `recaptcha` is the server's { siteKey, scriptUrl }
// (User::RegistrationsController.recaptcha_widget), or null when it skips the
// check. The script loads once and renders the widget it finds on the page.
// Open House's account screen renders this same widget.
// The script renders the widgets on the page when it loads; a widget mounted
// after that (a dialog opened again) is rendered here, its id kept on the
// element for recaptchaAnswer and resetRecaptcha.
export function RecaptchaWidget({ recaptcha }) {
  const box = useRef(null)

  useEffect(() => {
    if (!recaptcha) return
    if (!document.querySelector("script[data-recaptcha]")) {
      const script = document.createElement("script")
      script.src = recaptcha.scriptUrl
      script.async = true
      script.defer = true
      script.dataset.recaptcha = "1"
      document.head.appendChild(script)
    } else if (box.current && !box.current.childElementCount && window.grecaptcha?.render) {
      box.current.dataset.widgetId = window.grecaptcha.render(box.current, { sitekey: recaptcha.siteKey })
    }
  }, [recaptcha])

  if (!recaptcha) return null
  return (
    <div className="flex justify-center">
      <div ref={box} className="g-recaptcha" data-sitekey={recaptcha.siteKey} />
    </div>
  )
}

// The answer and the reset of the widget inside `scope` (the first rendered one without an id).
const widgetIn = (scope) => scope?.querySelector?.(".g-recaptcha")?.dataset.widgetId
export const recaptchaAnswer = (scope) => window.grecaptcha?.getResponse?.(widgetIn(scope)) || ""
export const resetRecaptcha = (scope) => window.grecaptcha?.reset?.(widgetIn(scope))

// The customer create-account page (P11-H3 Welcome Mat). It posts as an
// ordinary browser form to Devise's registrations#create with the legacy
// field names, so the server's checks (reCAPTCHA, taken email, existing
// freelancer, sanctioned country) and its redirects keep working unchanged.
// Blank names re-render it with `values` kept and `errors` per field.
//
// `recaptcha` is null when the server skips verification (test, or the E2E
// bypass) or has no site key; otherwise the real widget renders and the
// browser posts its `g-recaptcha-response` with the form. The affiliate code
// comes from the same local-storage record the legacy pages write.
export default function SignUp({
  action,
  email = "",
  values = {},
  errors = {},
  referralCode = null,
  creationType,
  recaptcha = null,
  openHouse = false,
  flash = {},
  links,
}) {
  const [password, setPassword] = useState("")
  const [confirmation, setConfirmation] = useState("")
  const [mismatch, setMismatch] = useState(false)
  const [newsletter, setNewsletter] = useState(true)
  const [affiliateCode, setAffiliateCode] = useState(null)

  useEffect(() => {
    // Reading `localStorage` itself throws when the browser denies site
    // storage; the page must still render and post a code from the URL.
    let storage = null
    try {
      storage = window.localStorage
    } catch {
      storage = null
    }
    setAffiliateCode(captureAffiliate(window.location.search, storage))
  }, [])

  const longEnough = password.length >= MIN_PASSWORD
  const checkMatch = () => setMismatch(confirmation !== "" && confirmation !== password)

  function handleSubmit(event) {
    if (confirmation === password) return
    event.preventDefault()
    setMismatch(true)
  }

  return (
    <AuthLayout panel="signup" swap={{ prompt: "Already have an account?", label: "Sign in", href: links.signIn }}>
      <Head title="Create your account · The Urban Writers" />

      <AuthHeading title="Create your account">
        Posting a project is free. You only pay once a freelancer accepts.
      </AuthHeading>

      {referralCode && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-[12px] border border-done/30 bg-done/10 py-2 pr-3 pl-2.5 text-[12.5px] font-semibold">
          <Gift className="size-[15px] shrink-0 text-done" strokeWidth={2} aria-hidden="true" />
          <span className="min-w-0 flex-1 leading-[1.4]">
            Referred by a friend · <b className="font-bold">5% off</b> your first order
          </span>
          <code className="ml-auto font-display font-bold tracking-[0.02em] text-done">{referralCode}</code>
        </div>
      )}

      {flash.notice && <AuthAlert tone="good">{flash.notice}</AuthAlert>}
      {flash.alert && <AuthAlert tone="bad">{flash.alert}</AuthAlert>}

      <form method="post" action={action} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <CsrfField />
        <input type="hidden" name="registration_type" value="customer" />
        <input type="hidden" name="customer_attributes[creation_type]" value={creationType} />
        {referralCode && <input type="hidden" name="referral_code" value={referralCode} />}
        {affiliateCode && <input type="hidden" name="affiliate[code]" value={affiliateCode} />}

        <TextField
          id="customer_first_name"
          name="customer_attributes[first_name]"
          label="First name"
          autoComplete="given-name"
          defaultValue={values.firstName}
          error={errors.firstName}
          autoFocus
          required
        />

        <TextField
          id="customer_last_name"
          name="customer_attributes[last_name]"
          label="Last name"
          autoComplete="family-name"
          defaultValue={values.lastName}
          error={errors.lastName}
          required
        />

        <TextField
          id="user_email"
          name="user[email]"
          type="email"
          label="Email"
          placeholder="you@example.com"
          autoComplete="email"
          defaultValue={email}
          required
        />

        <PasswordField
          id="user_password"
          name="user[password]"
          label="Password"
          placeholder="At least 6 characters"
          autoComplete="new-password"
          minLength={MIN_PASSWORD}
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onBlur={checkMatch}
          hint={
            <span
              data-met={longEnough}
              className={cn("inline-flex items-center gap-[5px]", longEnough && "text-muted-foreground")}
            >
              <Check
                className={cn("size-[13px]", longEnough ? "text-done" : "text-line-2")}
                strokeWidth={2.4}
                aria-hidden="true"
              />
              At least 6 characters
            </span>
          }
        />

        <PasswordField
          id="user_password_confirmation"
          name="user[password_confirmation]"
          label="Confirm password"
          placeholder="Repeat your password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD}
          required
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          onBlur={checkMatch}
          error={mismatch ? "Passwords don’t match." : undefined}
        />

        <label
          htmlFor="accepts_marketing"
          className={cn(
            "flex cursor-pointer items-start gap-[11px] rounded-[12px] border bg-secondary px-[14px] py-[13px]",
            newsletter ? "border-line-2" : "border-border"
          )}
        >
          {/* An unchecked box posts nothing; the hidden false is what Rails reads then. */}
          <input type="hidden" name="customer_attributes[accepts_marketing]" value="false" />
          <Checkbox
            id="accepts_marketing"
            name="customer_attributes[accepts_marketing]"
            value="true"
            checked={newsletter}
            onCheckedChange={(checked) => setNewsletter(checked === true)}
            className="mt-[1px] size-5 rounded-[6px] border-[1.5px] border-line-2 bg-card data-[state=checked]:border-foreground data-[state=checked]:bg-foreground data-[state=checked]:text-background"
          />
          <span className="text-[13px] font-medium leading-[1.45]">
            Send me writing tips and offers
            <small className="mt-0.5 block text-[12px] font-normal text-ink-3">
              Subscribe to the newsletter and save 5% on your first order.
            </small>
          </span>
        </label>

        <RecaptchaWidget recaptcha={recaptcha} />

        <Button
          type="submit"
          className="mt-[6px] h-auto w-full rounded-[11px] px-4 py-3 text-[14px] font-semibold"
        >
          Create account
        </Button>

        <p className="text-center text-[12px] leading-[1.45] text-ink-3">
          By continuing you agree to our{" "}
          <a href={TERMS_URL} className="underline underline-offset-2">
            Terms
          </a>{" "}
          and{" "}
          <a href={PRIVACY_URL} className="underline underline-offset-2">
            Privacy Policy
          </a>
          .
        </p>
        <p className="text-center text-[12px] leading-[1.45] text-ink-3">
          {openHouse
            ? "Next: verify your email, accept the usage agreement, and then you’ll land on your dashboard."
            : "Next: verify your email, accept the usage agreement, and finish your profile."}
        </p>
      </form>

      <p className="text-center text-[13.5px] text-muted-foreground">
        Already have an account?{" "}
        <a href={links.signIn} className="font-bold text-foreground underline decoration-line-2 underline-offset-[3px] hover:decoration-foreground">
          Sign in
        </a>
      </p>

      <div className="flex items-center gap-3 rounded-[12px] border border-border bg-secondary px-[14px] py-3 max-[560px]:flex-wrap">
        <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[9px] border border-border bg-card text-muted-foreground">
          <Briefcase className="size-4" strokeWidth={1.9} aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1 text-[12.5px] leading-[1.45] text-muted-foreground">
          <b className="block text-[13px] font-bold text-foreground">Joining as a freelancer?</b>
          Writers, editors, designers and narrators apply first. Approved applicants then{" "}
          <a href={links.freelancerSignUp} className={LINK_CLASS}>
            create a freelancer account
          </a>
          .
        </span>
        <Button asChild variant="outline" size="sm" className="shrink-0 rounded-[9px] max-[560px]:w-full">
          <a href={links.apply}>Apply</a>
        </Button>
      </div>
    </AuthLayout>
  )
}
